import { supabase } from '@/integrations/supabase/client';
import { NutritionService } from '@/services/NutritionService';
import { NutritionActivityCorrelationService } from '@/services/NutritionActivityCorrelationService';
import { NutritionDataType, NutritionQueryData } from '@/types/nutrition';
import { getTodayInUserTimezone } from '@/lib/utils/timezone';

export class NutritionDataService {
  private static async getDailySummary(userId: string) {
    const today = getTodayInUserTimezone();
    const response = await NutritionService.getDailySummary(userId, today);
    return response?.data || null;
  }

  private static async getMealsToday(userId: string) {
    const today = getTodayInUserTimezone();
    const response = await NutritionService.getMeals(userId, today, today);
    return response?.data || [];
  }

  private static async getMealsForDate(userId: string, date: string) {
    const response = await NutritionService.getMeals(userId, date, date);
    return response?.data || [];
  }

  private static async getMealsForDateRange(userId: string, startDate: string, endDate: string) {
    const response = await NutritionService.getMeals(userId, startDate, endDate);
    return response?.data || [];
  }

  private static async getGoals(userId: string) {
    const response = await NutritionService.getCurrentGoals(userId);
    return response?.data || null;
  }

  private static async getInsights(userId: string) {
    const [upcoming, adaptive] = await Promise.all([
      NutritionActivityCorrelationService.getUpcomingWorkoutRecommendations(userId),
      NutritionActivityCorrelationService.generateAdaptiveRecommendations(userId)
    ]);
    
    return [...upcoming, ...adaptive].sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }

  private static async getPerformanceCorrelations(userId: string) {
    return NutritionActivityCorrelationService.getPerformanceCorrelations(userId, 14);
  }

  private static async getNetCalories(userId: string) {
    return NutritionActivityCorrelationService.getNetCalorieAnalysis(userId, 7);
  }

  private static async getWeeklyConsistency(userId: string) {
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const weekAgoString = weekAgo.toISOString().split('T')[0]; // Keep this as UTC for historical data comparison
    
    const { data } = await supabase
      .from('nutrition_daily_summary')
      .select('date')
      .eq('user_id', userId)
      .gte('date', weekAgoString);

    const daysTracked = data?.length || 0;
    const percentage = Math.round((daysTracked / 7) * 100);
    
    return { percentage, daysTracked };
  }

  static async getData<T extends NutritionDataType>(
    type: T, 
    userId: string,
    params?: { date?: string; startDate?: string; endDate?: string }
  ): Promise<NutritionQueryData[T]> {
    switch (type) {
      case 'daily-summary':
        return this.getDailySummary(userId) as Promise<NutritionQueryData[T]>;
      case 'meals-today':
        return this.getMealsToday(userId) as Promise<NutritionQueryData[T]>;
      case 'meals-for-date':
        if (!params?.date) throw new Error('Date parameter required for meals-for-date');
        return this.getMealsForDate(userId, params.date) as Promise<NutritionQueryData[T]>;
      case 'meals-for-range':
        if (!params?.startDate || !params?.endDate) throw new Error('Date range parameters required for meals-for-range');
        return this.getMealsForDateRange(userId, params.startDate, params.endDate) as Promise<NutritionQueryData[T]>;
      case 'goals':
        return this.getGoals(userId) as Promise<NutritionQueryData[T]>;
      case 'insights':
        return this.getInsights(userId) as Promise<NutritionQueryData[T]>;
      case 'performance-correlations':
        return this.getPerformanceCorrelations(userId) as Promise<NutritionQueryData[T]>;
      case 'net-calories':
        return this.getNetCalories(userId) as Promise<NutritionQueryData[T]>;
      case 'weekly-consistency':
        return this.getWeeklyConsistency(userId) as Promise<NutritionQueryData[T]>;
      default:
        throw new Error(`Unknown nutrition data type: ${type}`);
    }
  }

  static invalidateUserData(userId: string, queryClient: any) {
    const queryKeys = [
      'daily-summary',
      'meals-today', 
      'goals',
      'insights',
      'performance-correlations',
      'net-calories',
      'weekly-consistency'
    ];

    queryKeys.forEach(key => {
      queryClient.invalidateQueries({ 
        queryKey: [key, userId],
        exact: false 
      });
    });
  }
}